import { alignMultisite4nkWebLineHostToPageLine } from "le-coffre-resources/dist/browserPublicHostPatterns.js";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";

const DEBUG_LOG_TEST_FQDNS = ["test.lecoffreio.4nkweb.com"] as const;

export function normalizeProtocol(value?: string): string {
	if (!value) return "";
	if (value.endsWith("://")) return value;
	if (value.endsWith(":")) return `${value}//`;
	if (value.includes("://")) return value;
	return `${value}://`;
}

export function ensureLeadingSlash(value: string): string {
	if (!value) return "/";
	return value.startsWith("/") ? value : `/${value}`;
}

export function trimTrailingSlash(value: string): string {
	return value.replace(/\/$/, "");
}

export function withDefault(value: string | undefined | null, fallback: string): string {
	if (typeof value !== "string") return fallback;
	if (value.trim().length === 0) return fallback;
	return value;
}

export function debugLog(label: string, payload: unknown): void {
	if (process.env.NODE_ENV === "development") {
		LoggerService.debug(`🔧 [${label}]`, { payload });
		return;
	}
	if (typeof window === "undefined") {
		return;
	}
	const h = window.location.hostname.toLowerCase();
	if (!(DEBUG_LOG_TEST_FQDNS as readonly string[]).includes(h)) {
		return;
	}
	LoggerService.debug(`🔧 [${label}]`, { payload });
}

export function splitPath(pathname: string | undefined): { inferredRoot?: string; inferredVersion?: string } {
	if (!pathname) return {};
	const clean = pathname.replace(/\/+$/, "");
	if (!clean || clean === "/") return {};
	const segments = clean.split("/").filter(Boolean);
	if (segments.length === 0) return {};
	let inferredVersion: string | undefined;
	let inferredRoot: string | undefined;
	const last = segments[segments.length - 1];
	if (last && /^v\d+$/i.test(last)) {
		inferredVersion = `/${last}`;
		segments.pop();
	}
	if (segments.length > 0) {
		inferredRoot = `/${segments.join("/")}`;
	}
	return { inferredRoot, inferredVersion };
}

/**
 * Aucune substitution d’hôte : la config (**`NEXT_PUBLIC_FRONT_APP_HOST`**, secrets, **`public/config`**) doit déjà viser le FQDN exact attendu pour la page (**`{env}.lecoffreio.4nkweb.com`** en v2 notaire).
 */
export function rewriteLegacyLecoffreIoFrontUrlOnV2NotaryPage(configuredFront: string): string {
	return configuredFront;
}

/**
 * Délègue à **`alignMultisite4nkWebLineHostToPageLine`** (`le-coffre-resources`) : même palier, deux lignes **`*.4nkweb.com`** strictes.
 */
export function rewriteBackApiHostWhenApiSubdomainMirrorsPage(browserHost: string, configuredHost: string): string {
	const next = alignMultisite4nkWebLineHostToPageLine(browserHost, configuredHost);
	const before = configuredHost.trim();
	const after = next.trim();
	if (after !== before) {
		LoggerService.warn("[FrontConfig] BACK_API_HOST realigned to page multisite line (cross-line API host corrected)", {
			pageHost: browserHost.trim().toLowerCase().split(":")[0],
			previousBackApiHost: before,
			alignedBackApiHost: after,
		});
	}
	return next;
}

export function normalizeHost(
	value: string | undefined,
	protocol: string,
	fallbackHost: string,
): { host: string; inferredRoot?: string; inferredVersion?: string } {
	if (!value) {
		return { host: fallbackHost };
	}

	const trimmed = value.trim();
	if (trimmed.length === 0) {
		return { host: fallbackHost };
	}

	try {
		const base = trimmed.includes("://") ? trimmed : `${protocol}${trimmed.replace(/^\/\//, "")}`;
		const url = new URL(base);
		const { inferredRoot, inferredVersion } = splitPath(url.pathname);
		return { host: url.host, inferredRoot, inferredVersion };
	} catch {
		const hostOnly = trimmed.split("/")[0];
		if (hostOnly) {
			return { host: hostOnly };
		}
		return { host: fallbackHost };
	}
}
